import { animated, useSprings } from '@react-spring/web'
import { useDrag } from '@use-gesture/react'
import { clamp } from 'lodash-es'
import React, { MouseEventHandler, useEffect, useRef, useState } from 'react'
import Button from '~/components/Button'
import { Caret } from '~/components/Icons'
import type { HTMLElementProps, Image } from '~/types'
import { cn, imageUrl, isTouchDevice, useWindowSize } from '~/utils'

export const ChangeSlide: React.FC<HTMLElementProps<'button'>> = ({ color, className, ...props }) => (
	<Button
		transparent
		className={cn('absolute top-0 h-full w-20 rounded-none text-5xl hover:opacity-70', color, className)}
		{...props}
	/>
)

export interface Props extends HTMLElementProps<'div'> {
	images: Image[]
	index: number
	className?: string
	onUpdate: (i: number) => void
	background?: 'cover' | 'contain' | 'backdrop'
	onTripleClick?: MouseEventHandler
	showControls?: boolean
	controlsColor?: string
}

export const Slideshow: React.FC<Props> = ({
	images,
	index = 0,
	className,
	onUpdate,
	onClick,
	onDoubleClick,
	onTripleClick,
	onKeyDown,
	children,
	showControls = true,
	controlsColor = 'text-gray-800',
	background = 'contain',
	...props
}) => {
	const ref = useRef<HTMLDivElement | null>(null)
	const [dragging, setDragging] = useState(false)
	const windowWidth = useWindowSize().width
	const width = ref.current?.clientWidth || windowWidth

	useEffect(() => {
		onUpdate(clamp(index, 0, images.length - 1))
	}, [index, onUpdate, images])

	const [springs, api] = useSprings(
		images.length,
		i => ({
			x: (i - index) * width,
			scale: 1
		}),
		[width, windowWidth, index]
	)

	const bind = useDrag(
		({ active, movement: [mx], direction: [xDir], cancel }) => {
			if (active && Math.abs(mx) > width / 4) {
				setDragging(false)
				onUpdate(index + (xDir > 0 ? -1 : 1))
				cancel()
			}
			api.start(i => {
				setDragging(true)
				const x = (i - index) * width + (active ? mx : 0)
				const scale = active ? 1 - Math.abs(mx) / width : 1
				return { x, scale }
			})
		},
		{ threshold: 5 }
	)

	const handleKey: React.KeyboardEventHandler<HTMLDivElement> = e => {
		if (e.key == 'ArrowLeft') onUpdate(index - 1)
		else if (e.key == 'ArrowRight') onUpdate(index + 1)
		if (onKeyDown) onKeyDown(e)
	}

	return (
		<div
			ref={ref}
			className={cn('relative h-full w-full cursor-move', className)}
			onKeyDown={handleKey}
			onClick={e => e.detail === 3 && onTripleClick && onTripleClick(e)}
			tabIndex={0}
			{...props}
		>
			{!isTouchDevice() && (
				<input onKeyDown={handleKey} className="m-0 block h-0 cursor-default border-none p-0 opacity-0" />
			)}
			{background === 'backdrop' && (
				<>
					{springs.map(({ x }, i) => (
						<animated.div
							key={getKey(images, i)}
							className="no-supports-backdrop:hidden absolute inset-0 bg-cover bg-center"
							style={{ x: x.to(Math.round), backgroundImage: `url(${imageUrl(images[i])})` }}
							role="img"
							aria-label={images[i]?.altText ?? undefined}
						/>
					))}
					<div className="absolute inset-0 backdrop-blur-lg backdrop-brightness-90" />
				</>
			)}
			{springs.map(({ x, scale }, i) => (
				<animated.div
					className="absolute h-full w-full touch-none"
					{...bind()}
					key={getKey(images, i)}
					style={{ x }}
				>
					<animated.div
						onClick={e => !dragging && onClick && onClick(e)}
						onMouseUp={_ => {
							// Prevent onClick handler from firing while dragging
							setTimeout(() => setDragging(false), 0)
						}}
						onDoubleClick={onDoubleClick}
						className={cn(
							'absolute inset-0 touch-none bg-center bg-no-repeat',
							background === 'cover' ? 'bg-cover' : 'bg-contain'
						)}
						style={{ scale, backgroundImage: `url(${imageUrl(images[i])})` }}
						role="img"
						aria-label={images[i]?.altText ?? undefined}
					/>
				</animated.div>
			))}
			<div
				className="contents"
				onClick={e => {
					e.stopPropagation()
					e.preventDefault()
					e.nativeEvent.stopImmediatePropagation()
				}}
			>
				{showControls && images.length > 1 && (
					<>
						<ChangeSlide className="left-0" onClick={_ => onUpdate(index - 1)} color={controlsColor}>
							<Caret className="rotate-90" />
						</ChangeSlide>
						<div className={`absolute bottom-4 z-10 w-full space-x-2 text-center ${controlsColor}`}>
							{images.map((_, i) => (
								<Button
									key={getKey(images, i)}
									secondary
									className={cn(
										'rounded-full border border-current p-1.5 text-inherit',
										'hover:border-current hover:bg-current hover:drop-shadow',
										i == index ? 'bg-current' : 'bg-transparent'
									)}
									onClick={() => onUpdate(i)}
								/>
							))}
						</div>
						<ChangeSlide className="right-0" onClick={_ => onUpdate(index + 1)} color={controlsColor}>
							<Caret className="-rotate-90" />
						</ChangeSlide>
					</>
				)}
			</div>
			{children}
		</div>
	)
}

function getKey(images: Image[], index: number): number | string {
	return images[index]?.id ?? index
}
